import { ApplicationElement, html } from '@beunity/lit-components';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Automatically truncates content and shows more button if it's too long.
 *
 * @prop maxLines - Truncate content to this lines
 * @prop collapseOn - <u>Show less</u> button will be shown when content can be truncated
 * @prop truncated - Internal property => Present if current state is truncated or not
 *
 * @slot default - For main content (body text)
 * @slot trigger - For 'More' button when truncated (optional)
 */

export default class Truncate extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];
  static translations = translations;
  static properties = {
    maxLines: { type: Number },
    collapseOn: { type: Boolean },
    truncated: { state: true, type: Boolean },
  };

  constructor() {
    super();
    this.maxLines = 3;
  }

  /** @internal */
  get innerEl() {
    return this.shadowRoot.querySelector('.inner');
  }

  /** @internal */
  get innerClass() {
    return {
      inner: true,
      truncated: this.truncated,
    };
  }

  /** @internal */
  get innerStyle() {
    return {
      '-webkit-line-clamp': this.maxLines,
    };
  }

  /** @internal */
  get canTruncate() {
    if (!this.innerEl) return;

    const currentDisplay = this.innerEl.style.display;

    this.innerEl.style.display = '-webkit-box';

    const temp = this.shouldTruncate;

    this.innerEl.style.display = currentDisplay;
    return temp;
  }

  /** @internal */
  get shouldTruncate() {
    return this.innerEl.scrollHeight > this.innerEl.clientHeight;
  }

  /** @internal */
  get shouldHideTrigger() {
    // Hide trigger if content is too short
    if (!this.canTruncate) {
      return true;
    }

    if (!this.collapseOn && !this.truncated) {
      return true;
    }

    return false;
  }

  onClick(e) {
    e.preventDefault();
    this.truncated = !this.truncated;
    this.innerEl.style.display = this.truncated ? '-webkit-box' : 'block';
  }

  onSlotChange() {
    this.truncated = this.shouldTruncate;
  }

  renderTrigger() {
    return html`
      <button class="bu-btn btn-link">
        <span>${this.truncated ? this.t('more') : this.t('less')}</span>
        <bu-icon name=${this.truncated ? 'chevron-down' : 'chevron-up'}>
        </bu-icon>
      </button>
    `;
  }

  render() {
    return html`
      <div
        class=${classMap(this.innerClass)}
        style=${styleMap(this.innerStyle)}>
        <slot @slotchange=${this.onSlotChange}></slot>
      </div>
      <slot
        name="trigger"
        @click=${this.onClick}
        .hidden=${this.shouldHideTrigger}>
        ${this.renderTrigger()}
      </slot>
    `;
  }
}

Truncate.registerTagName('bu-truncate');

customElements.get('bu-truncate') ||
  customElements.define('bu-truncate', Truncate);
